import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH,
  AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT,
  AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y,
  AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX,
  AUTOBAHN_LANE_BOUNDARY_LABELS,
  buildAutobahnLaneSlotsFromBoundaries,
  buildAutobahnLaneBoundaryGuideLines,
  autobahnLaneSimonSlotToSvgPoints,
} from './autobahnLaneSimonLayout';
import './AutobahnLaneBoundaryEditor.css';

const LANE_DEV_COLORS = ['rgba(250, 204, 21, 0.22)', 'rgba(56, 189, 248, 0.22)', 'rgba(167, 139, 250, 0.22)', 'rgba(248, 113, 113, 0.22)'];

/**
 * @param {HTMLElement} el
 * @param {number} clientX
 * @returns {number}
 */
function clientToLogicalX(el, clientX) {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0) {
    throw new Error('AutobahnLaneBoundaryEditor: container has no size');
  }
  const x = ((clientX - rect.left) / rect.width) * AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH;
  return Math.max(0, Math.min(AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH, x));
}

/**
 * @param {number[]} boundaries
 * @param {number} boundaryIndex
 * @param {number} nextX
 * @returns {number[]}
 */
function applyBoundaryDrag(boundaries, boundaryIndex, nextX) {
  const next = [...boundaries];
  const minX = boundaryIndex === 0 ? 0 : next[boundaryIndex - 1] + AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX;
  const maxX =
    boundaryIndex === 4
      ? AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH
      : next[boundaryIndex + 1] - AUTOBAHN_LANE_SIMON_MIN_BOUNDARY_GAP_PX;
  next[boundaryIndex] = Math.round(Math.max(minX, Math.min(maxX, nextX)));
  return next;
}

/**
 * @param {object} props
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {number[]} props.boundaries
 * @param {(boundaries: number[]) => void} props.onBoundariesChange
 */
export default function AutobahnLaneBoundaryEditor({ containerRef, boundaries, onBoundariesChange }) {
  const [draggingBoundaryIndex, setDraggingBoundaryIndex] = useState(null);

  const laneSlots = useMemo(() => buildAutobahnLaneSlotsFromBoundaries(boundaries), [boundaries]);
  const guideLines = useMemo(() => buildAutobahnLaneBoundaryGuideLines(boundaries), [boundaries]);

  useEffect(() => {
    if (draggingBoundaryIndex == null) {
      return undefined;
    }
    const onMove = (e) => {
      const el = containerRef?.current;
      if (!el) return;
      const lx = clientToLogicalX(el, e.clientX);
      onBoundariesChange(applyBoundaryDrag(boundaries, draggingBoundaryIndex, lx));
    };
    const onUp = () => setDraggingBoundaryIndex(null);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [draggingBoundaryIndex, containerRef, boundaries, onBoundariesChange]);

  const beginDrag = useCallback((e, boundaryIndex) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.currentTarget.setPointerCapture === 'function' && e.pointerId != null) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setDraggingBoundaryIndex(boundaryIndex);
    const el = containerRef?.current;
    if (!el) return;
    const lx = clientToLogicalX(el, e.clientX);
    onBoundariesChange(applyBoundaryDrag(boundaries, boundaryIndex, lx));
  }, [containerRef, boundaries, onBoundariesChange]);

  return (
    <div className="autobahn-lane-boundary-editor" aria-hidden={false}>
      <svg
        className="autobahn-lane-boundary-editor-svg"
        viewBox={`0 0 ${AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH} ${AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT}`}
        preserveAspectRatio="xMidYMid meet"
      >
        {laneSlots.map((slot) => (
          <polygon
            key={`lane-fill-${slot.id}`}
            points={autobahnLaneSimonSlotToSvgPoints(slot)}
            className="autobahn-lane-boundary-editor-lane-fill"
            fill={LANE_DEV_COLORS[slot.id]}
          />
        ))}
        {guideLines.map((line) => (
          <line
            key={`guide-${line.boundaryIndex}`}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            className="autobahn-lane-boundary-editor-guide"
          />
        ))}
      </svg>
      {boundaries.map((xBottom, boundaryIndex) => {
        const leftPct = (xBottom / AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH) * 100;
        const topPct = (AUTOBAHN_LANE_SIMON_PERSPECTIVE_ROAD_BOTTOM_Y / AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT) * 100;
        return (
          <button
            key={`boundary-handle-${boundaryIndex}`}
            type="button"
            className="autobahn-lane-boundary-editor-handle"
            style={{ left: `${leftPct}%`, top: `${topPct}%` }}
            aria-label={AUTOBAHN_LANE_BOUNDARY_LABELS[boundaryIndex]}
            onPointerDown={(e) => beginDrag(e, boundaryIndex)}
          >
            <span className="autobahn-lane-boundary-editor-handle-line" />
            <span className="autobahn-lane-boundary-editor-handle-label">{boundaryIndex}</span>
            <span className="autobahn-lane-boundary-editor-handle-hint">{AUTOBAHN_LANE_BOUNDARY_LABELS[boundaryIndex]}</span>
          </button>
        );
      })}
      {laneSlots.map((slot) => {
        const [, , rightBottom, leftBottom] = slot.points;
        const cx = (leftBottom[0] + rightBottom[0]) / 2;
        const cy = (leftBottom[1] + rightBottom[1]) / 2;
        const leftPct = (cx / AUTOBAHN_LANE_SIMON_LOGICAL_WIDTH) * 100;
        const topPct = (cy / AUTOBAHN_LANE_SIMON_LOGICAL_HEIGHT) * 100;
        return (
          <span
            key={`lane-num-${slot.id}`}
            className="autobahn-lane-boundary-editor-lane-number"
            style={{ left: `${leftPct}%`, top: `${topPct}%` }}
          >
            Lane {slot.id + 1}
          </span>
        );
      })}
    </div>
  );
}
